import type { EnrichedParticipants, Runner } from '@ps/isp-web-services-types';
import { groupBy, isEmpty, unzip } from 'lodash';

export const runnerNameMatchPattern = /\(([-+]?\d+([.,]\d+)?)\)/;

const extractValue = (name: string) => name.match(runnerNameMatchPattern)?.[1];

const mapRunners = (runners: Runner[]) =>
  runners.reduce<Record<string, Runner>>((acc, runner) => {
    const val = extractValue(runner.runnerName);
    if (val) acc[val] = runner;
    return acc;
  }, {});

const invertSign = (val: string) => (val[0] === '+' ? '-' : '+') + val.slice(1);

const getNumericValue = (item: Runner | string) => {
  if (typeof item === 'string') {
    return parseFloat(item.replace(',', '.'));
  }

  const match = item.runnerName.match(runnerNameMatchPattern);
  return match ? parseFloat(match[1].replace(',', '.')) : NaN;
};

export const groupRunners = (
  runners: Runner[] | undefined,
  enrichedParticipants: EnrichedParticipants | undefined
) => {
  if (isEmpty(runners) || isEmpty(enrichedParticipants)) return [[], []];

  const grouped = groupBy(runners, 'resultType');
  let homeRunners = grouped.HOME;
  let awayRunners = grouped.AWAY;

  const isAwayFirst =
    enrichedParticipants?.[0]?.type === 'AWAY' && enrichedParticipants[1]?.type === 'HOME';

  if (isAwayFirst) {
    [homeRunners, awayRunners] = [awayRunners, homeRunners];
  }

  const homeMap = mapRunners(homeRunners);
  const awayMap = mapRunners(awayRunners);

  const parseNumber = (val: string) => parseFloat(val.replace(',', '.'));

  const allValues = Array.from(new Set([...Object.keys(homeMap), ...Object.keys(awayMap)])).sort(
    (a, b) => {
      const numA = parseNumber(a);
      const numB = parseNumber(b);
      return numA - numB;
    }
  );

  const homeResult = allValues
    .map((val) => {
      const inverted = invertSign(val);
      if (allValues.includes(inverted)) {
        return homeMap[val] ?? val;
      }
      return [homeMap[val] ?? val, inverted];
    })
    .flat();

  const awayResult = allValues
    .map((val) => {
      const inverted = invertSign(val);
      if (allValues.includes(inverted)) {
        return awayMap[inverted] ?? inverted;
      }
      return [inverted, awayMap[val] ?? val];
    })
    .flat();

  const filtered = homeResult
    .map((home, index) => [home, awayResult[index]])
    .filter(([home, away]) => !(typeof home === 'string' && typeof away === 'string'))
    .sort((a, b) => getNumericValue(b[0]) - getNumericValue(a[0]));

  return unzip(filtered);
};
